import { Alert, Card, Flex, Form, Input, Spin } from 'antd'
import dayjs from 'dayjs'
import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  useCounterpartServiceGetInternalInstitutionEndpoint,
  useInstitutionServiceGetInstitutionEndpoint,
  useTreatmentServiceCreateSidaCaseEndpoint,
  useTreatmentServiceGetSidaCaseEndpoint,
  useTreatmentServiceUpdateSidaCaseEndpoint
} from 'src/api/queries'
import {
  ApiError,
  EpidemiologyCaseForm058E,
  PersonIdentityStatus
} from 'src/api/requests'
import { FormFooter, message, PageBreadcrumbs } from 'src/components'
import { usePatientBreadcrumb } from 'src/features/patients/hooks/usePatientBreadcrumb'
import {
  EpidemiologyFinalDiagnosticCard,
  EpidemiologyNotificationCard,
  EpidemiologyPatientInformationCard,
  EpidemiologyPrimaryDiagnosticCard,
  EpidemiologyRegistrationCard,
  EpidemiologySymptomsCard
} from 'src/features/persons/pages/epidemiology-cases/components'
import { useLatestConsultationDiagnosesPrefill } from 'src/features/persons/pages/epidemiology-cases/useLatestConsultationDiagnosesPrefill'
import { useRegistrationPrefill } from 'src/features/persons/pages/epidemiology-cases/useRegistrationPrefill'
import { useUser } from 'src/hooks'
import { yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formatDates, formErrorHandler, parseDates } from 'src/utils'

import { SidaDiagnosisCard, SidaMedicalPersonnelCard } from '../components'

const toIds = (items?: Array<{ id?: string }> | null) =>
  items?.map((item) => item.id).filter((id): id is string => !!id) ?? []

export const AddSidaNotificationPage = () => {
  const { t } = useLocales()
  const navigate = useNavigate()
  const { personId } = useParams()

  const {
    breadcrumbItems: items,
    isLoadingPerson,
    person
  } = usePatientBreadcrumb(personId)

  const { userData } = useUser()
  const currentLegalEntityId = userData?.currentLegalEntityId

  const [form] = Form.useForm()

  const { data: existing, isLoading: isLoadingCase } =
    useTreatmentServiceGetSidaCaseEndpoint(
      { personId: String(personId) },
      [personId],
      { enabled: !!personId, retry: false }
    )

  const isEdit = !!existing?.id

  const { data: internalInstitution, isLoading: isLoadingInternalInstitution } =
    useCounterpartServiceGetInternalInstitutionEndpoint(
      { id: String(currentLegalEntityId) },
      [currentLegalEntityId],
      { enabled: !!currentLegalEntityId }
    )

  useLatestConsultationDiagnosesPrefill(
    form,
    personId,
    !isLoadingCase && !isEdit,
    person,
    { name: internalInstitution?.name, code: internalInstitution?.idno }
  )

  const institutionId = internalInstitution?.institutionId

  const {
    data: currentInstitution,
    isLoading: isLoadingInstitution,
    isError: isInstitutionError
  } = useInstitutionServiceGetInstitutionEndpoint(
    { id: String(institutionId) },
    [institutionId],
    { enabled: !!institutionId, retry: false }
  )

  // Edit prefill: SIDA-specific fields, the epidemiology form and notes.
  useEffect(() => {
    if (!existing) return

    if (existing.form) {
      form.setFieldsValue({
        sidaStageDate: existing.form.aidsStageEstablishedDate
          ? dayjs(existing.form.aidsStageEstablishedDate)
          : undefined,
        sidaStageReasonIds: existing.form.aidsEstablishmentReasonIds,
        arvTreatmentAtDiagnosis: existing.form.hasArvTreatmentAtDiagnosis,
        statisticsReportDate: existing.form.cspReportingDate
          ? dayjs(existing.form.cspReportingDate)
          : undefined
      })
    }

    if (existing.epidemiologyForm) {
      form.setFieldsValue({ form: parseDates(existing.epidemiologyForm) })
    }

    if (existing.notes != null) {
      form.setFieldValue('notes', existing.notes)
    }
  }, [existing])

  // Registration prefill; on edit only the update date is moved to now.
  // Declared after the edit prefill so it runs on top of the saved form.
  useRegistrationPrefill(form, userData, !isLoadingCase, isEdit)

  // Notifier prefill (create only).
  useEffect(() => {
    if (isEdit) return
    const fullName = [userData?.firstName, userData?.lastName]
      .filter(Boolean)
      .join(' ')
    if (!fullName) return
    form.setFieldValue(['form', 'notification', 'notifierFullName'], fullName)
  }, [userData?.firstName, userData?.lastName, isEdit])

  // Notification institution prefill from the current institution (create only).
  useEffect(() => {
    if (isEdit || !currentInstitution) return

    form.setFieldsValue({
      form: {
        notification: {
          institution: {
            id: currentInstitution.id,
            title: currentInstitution.name,
            code: currentInstitution.institutionCode,
            medicalAssistanceType:
              currentInstitution.medicalAssistanceTypes?.[0],
            classification: currentInstitution.institutionClassificationIds?.[0]
              ? { id: currentInstitution.institutionClassificationIds[0] }
              : undefined,
            address: {
              country: currentInstitution.address?.country,
              locality: currentInstitution.address?.locality,
              cuatmLocalityId: currentInstitution.address?.cuatmLocality?.id,
              street: currentInstitution.address?.street,
              streetNumber: currentInstitution.address?.unitNumber
            },
            phoneNumber: currentInstitution.contactInfo?.phone
          }
        },
        registration: {
          institutionTitle: currentInstitution.name
        }
      }
    })
  }, [currentInstitution, isEdit])

  // Fallback notification institution prefill (create only).
  useEffect(() => {
    if (isEdit || currentInstitution || !internalInstitution) return

    form.setFieldsValue({
      form: {
        notification: {
          institution: {
            id: internalInstitution.institutionId,
            title: internalInstitution.name,
            address: {
              country: internalInstitution.address?.country,
              locality: internalInstitution.address?.locality,
              cuatmLocalityId: internalInstitution.address?.cuatmLocality?.id,
              street: internalInstitution.address?.street,
              streetNumber: internalInstitution.address?.unitNumber
            },
            phoneNumber: internalInstitution.contactInfo?.phone
          }
        },
        registration: {
          institutionTitle: internalInstitution.name
        }
      }
    })
  }, [internalInstitution, currentInstitution, isEdit])

  // Patient prefill from the person record (create only).
  useEffect(() => {
    if (isEdit || !person) return

    form.setFieldsValue({
      form: {
        patient: {
          identityStatus: person.isAnonymous
            ? PersonIdentityStatus.ANONYMOUS
            : PersonIdentityStatus.IDENTIFIED,
          firstName: person.firstName,
          lastName: person.lastName,
          idnp: person.idnp,
          birthDate: person.birthDate ? dayjs(person.birthDate) : undefined,
          gender: person.gender,
          citizenship: person.citizenship,
          currentAddress: {
            country: person.currentAddress?.country,
            street: person.currentAddress?.street,
            streetNumber: person.currentAddress?.streetNumber,
            apartmentNumber: person.currentAddress?.apartmentNumber,
            cuatmLocality: person?.currentAddress?.cuatmLocality?.id
              ? {
                  id: person.currentAddress.cuatmLocality.id,
                  name: person.currentAddress.cuatmLocality.translation
                }
              : undefined
          },
          residenceAddress: {
            country: person.residenceAddress?.country,
            street: person.residenceAddress?.street,
            streetNumber: person.residenceAddress?.streetNumber,
            apartmentNumber: person.residenceAddress?.apartmentNumber,
            cuatmLocality: person?.residenceAddress?.cuatmLocality?.id
              ? {
                  id: person.residenceAddress.cuatmLocality.id,
                  name: person.residenceAddress.cuatmLocality.translation
                }
              : undefined
          }
        }
      }
    })
  }, [person, isEdit])

  const { mutate: create, isPending: isCreating } =
    useTreatmentServiceCreateSidaCaseEndpoint({
      onError: (err: ApiError) => formErrorHandler(err.body.errors),
      onSuccess: () => {
        message.success(t('sida_notification.success_create'))
        navigate(paths.patients.sidaNotification.details(String(personId)))
      }
    })

  const { mutate: update, isPending: isUpdating } =
    useTreatmentServiceUpdateSidaCaseEndpoint({
      onError: (err: ApiError) => formErrorHandler(err.body.errors),
      onSuccess: () => {
        message.success(t('sida_notification.success_update'))
        navigate(paths.patients.sidaNotification.details(String(personId)))
      }
    })

  const breadcrumbItems = [
    ...items,
    {
      title: isEdit ? t('sida_notification.view') : t('sida_notification.add')
    }
  ]

  const onSave = async () => {
    await form.validateFields()
    const values = form.getFieldsValue(true)

    const formValues = values.form ?? {}
    const primary = formValues.primaryDiagnostic ?? {}
    const final = formValues.finalDiagnostic ?? {}

    const institutionPayload = currentInstitution
      ? {
          id: currentInstitution.id,
          title: currentInstitution.name,
          code: currentInstitution.institutionCode,
          medicalAssistanceType: currentInstitution.medicalAssistanceTypes?.[0],
          classification: currentInstitution.institutionClassificationIds?.[0]
            ? { id: currentInstitution.institutionClassificationIds[0] }
            : undefined,
          address: {
            country: currentInstitution.address?.country,
            locality: currentInstitution.address?.locality,
            cuatmLocalityId: currentInstitution.address?.cuatmLocality?.id,
            street: currentInstitution.address?.street,
            streetNumber: currentInstitution.address?.unitNumber
          },
          phoneNumber: currentInstitution.contactInfo?.phone
        }
      : formValues.notification?.institution

    const patient = formValues.patient ?? {}

    const epidemiologyForm = formatDates({
      ...formValues,
      notification: {
        ...(formValues.notification ?? {}),
        institution: institutionPayload
      },
      patient: {
        ...patient,
        socialStatus: person?.socialStatus,
        currentAddress: patient.currentAddress,
        residenceAddress: patient.residenceAddress
      }
    }) as EpidemiologyCaseForm058E

    const aidsStageEstablishedDate = dayjs(values.sidaStageDate).format(
      yearMonthDaySlash
    )
    const aidsEstablishmentReasonIds = values.sidaStageReasonIds ?? []
    const hasArvTreatmentAtDiagnosis = values.arvTreatmentAtDiagnosis ?? false
    const cspReportingDate = values.statisticsReportDate
      ? dayjs(values.statisticsReportDate).format(yearMonthDaySlash)
      : null
    const notes = values.notes ?? null

    if (isEdit) {
      update({
        id: String(existing.id),
        requestBody: {
          aidsStageEstablishedDate,
          aidsEstablishmentReasonIds,
          hasArvTreatmentAtDiagnosis,
          epidemiologyForm,
          cspReportingDate,
          notes
        }
      })
    } else {
      create({
        requestBody: {
          personId: String(personId),
          aidsStageEstablishedDate,
          aidsEstablishmentReasonIds,
          hasArvTreatmentAtDiagnosis,
          preliminaryDiagnosticIds: toIds(primary.diagnoses),
          finalDiagnosticIds: toIds(final.diagnoses),
          dateOfDiseaseOnset: primary.dateOfDiseaseOnset
            ? dayjs(primary.dateOfDiseaseOnset).format(yearMonthDaySlash)
            : null,
          diagnosisDate: final.diagnosisDate
            ? dayjs(final.diagnosisDate).format(yearMonthDaySlash)
            : null,
          epidemiologyForm,
          notes,
          cspReportingDate
        }
      })
    }
  }

  if (isLoadingPerson || isLoadingCase) return <Spin spinning fullscreen />

  const showInstitutionError = !isEdit && isInstitutionError

  return (
    <PageBreadcrumbs
      title={isEdit ? t('sida_notification.view') : t('sida_notification.add')}
      items={breadcrumbItems}
      showBack
    >
      {showInstitutionError ? (
        <Alert
          type="warning"
          showIcon
          message={t('epidemiology.no_institution_access_title')}
          description={t('epidemiology.no_institution_access_description')}
        />
      ) : (
        <Form layout="vertical" requiredMark={false} form={form}>
          <Flex vertical gap={24}>
            <EpidemiologyNotificationCard />
            <EpidemiologyPrimaryDiagnosticCard form={form} />
            <EpidemiologyPatientInformationCard form={form} person={person} />
            <EpidemiologyRegistrationCard />
            <EpidemiologySymptomsCard form={form} />
            <EpidemiologyFinalDiagnosticCard form={form} />

            <SidaDiagnosisCard />
            <SidaMedicalPersonnelCard
              userData={userData}
              savedDoctorName={existing?.form?.doctorFullName}
            />

            <Card>
              <Form.Item
                name="notes"
                label={t('form.notes')}
                rules={[{ max: 500 }]}
              >
                <Input.TextArea rows={4} placeholder={t('form.notes')} />
              </Form.Item>
            </Card>
          </Flex>

          <FormFooter
            onCancel={() => navigate(-1)}
            onSave={() => void onSave()}
            saveConfirmTitle={t('sida_notification.confirm_save')}
            saveLabel={t('sida_notification.save')}
            loading={
              isCreating ||
              isUpdating ||
              (!isEdit &&
                (isLoadingInternalInstitution || isLoadingInstitution))
            }
          />
        </Form>
      )}
    </PageBreadcrumbs>
  )
}
